import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import _ from 'lodash'

import Mesures, { MesuresProps } from '@/pages/diagnostic/components/Mesures'
import CommentField from '@/pages/diagnostic/components/CommentField'
import MedconnectDevicesDialogContainer from '@/pages/diagnostic/containers/MedconnectDevicesDialogContainer'
import { consultationOfflineStore, OfflineConsultation } from '@/core/offline'

type OfflinePC300MesuresProps = {
    consultation: OfflineConsultation
    onConsultationUpdate: (consultation: OfflineConsultation) => void
    deviceTracker?: boolean
    setDeviceTracker?: (value: boolean) => void
    pcStatus?: boolean
}

export function OfflinePC300Mesures({
    consultation,
    onConsultationUpdate,
    deviceTracker = false,
    setDeviceTracker,
    pcStatus = false
}: OfflinePC300MesuresProps) {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const [isUpdating, setIsUpdating] = useState(false)
    const [medconnectDevicesOpen, setMedconnectDevicesOpen] = useState<boolean>(false)

    const mesures = useMemo(() => _.sortBy(consultation?.mesures || [], 'order'), [consultation])

    const handleUpdateComment = async (comment: string) => {
        if (!consultation._id) return

        try {
            setIsUpdating(true)
            const updatedConsultation = await consultationOfflineStore.updateConsultation(consultation._id, {
                commentaire: comment
            })
            onConsultationUpdate(updatedConsultation)
            enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
        } finally {
            setIsUpdating(false)
        }
    }

    const handleUpdateMesures = async (mesures: MesuresProps['mesures']) => {
        if (!consultation._id) return

        try {
            setIsUpdating(true)
            const updatedConsultation = await consultationOfflineStore.updateConsultation(consultation._id, {
                mesures
            })
            onConsultationUpdate(updatedConsultation)
            enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
        } finally {
            setIsUpdating(false)
        }
    }

    const handleSaveMesures = async (csv: string) => {
        // In offline mode, we could save this as a document or just show success
        // For now, we'll just show success since we can't export files easily in offline mode
        enqueueSnackbar(t('notification.consultation.createMesuresCSV.success'), { variant: 'success' })
    }

    const handleOpenDevices = async () => {
        if (consultation._id) {
            setMedconnectDevicesOpen(true)
        }
    }

    const handleCloseDevices = () => {
        setMedconnectDevicesOpen(false)
    }

    return (
        <>
            <Mesures
                mesures={mesures}
                pcStatus={pcStatus}
                onMesuresChange={handleUpdateMesures}
                onMesuresSave={handleSaveMesures}
                onOpenDevices={handleOpenDevices}
                readOnly={isUpdating}
            />
            <CommentField
                comment={consultation?.commentaire || ''}
                onCommentChange={handleUpdateComment}
                readOnly={isUpdating}
            />
            {consultation._id && (
                <MedconnectDevicesDialogContainer
                    open={medconnectDevicesOpen}
                    onClose={handleCloseDevices}
                    consultationId={consultation._id}
                />
            )}
        </>
    )
}

export default OfflinePC300Mesures 